/**
 * Where the package page keeps a link's credentials between visits: the link secret, and the session
 * a correct code returned, per share, in this browser only.
 *
 * Only `{secret, session, expiresAt}` is ever written — nothing of the package itself — and it never
 * goes into Redux, so a serialised store or a devtools dump carries no credential. An entry past the
 * link's expiry is removed the moment it is read, and a refused read removes it through `forget`.
 *
 * `localStorage` rather than `sessionStorage`, because the recipient reopens the same link later in
 * the same browser. When storage is unavailable or throws (private windows, blocked site data, the
 * server render), a memory copy keeps the credentials for this visit only.
 */

const KEY_PREFIX = 'skillfitPackage:';

export const storageKeyFor = shareId => `${KEY_PREFIX}${shareId}`;

const defaultStorage = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

export const createPackageLinkStore = ({ storage } = {}) => {
  const memory = {};
  const storageOf = () => (storage === undefined ? defaultStorage() : storage);

  const readRaw = key => {
    const s = storageOf();
    if (s) {
      try {
        const value = s.getItem(key);
        if (value !== null && value !== undefined) return value;
        return Object.prototype.hasOwnProperty.call(memory, key) ? memory[key] : null;
      } catch (e) {
        // fall through to memory
      }
    }
    return Object.prototype.hasOwnProperty.call(memory, key) ? memory[key] : null;
  };

  const writeRaw = (key, value) => {
    const s = storageOf();
    if (s) {
      try {
        s.setItem(key, value);
        delete memory[key];
        return;
      } catch (e) {
        // fall through to memory
      }
    }
    memory[key] = value;
  };

  const removeRaw = key => {
    delete memory[key];
    const s = storageOf();
    if (!s) return;
    try {
      s.removeItem(key);
    } catch (e) {
      // nothing more can be done; the memory copy is already gone
    }
  };

  const readEntry = shareId => {
    const key = storageKeyFor(shareId);
    const raw = readRaw(key);
    if (raw === null) return null;
    let entry;
    try {
      entry = JSON.parse(raw);
    } catch (e) {
      entry = null;
    }
    if (!entry || typeof entry !== 'object' || typeof entry.secret !== 'string' || !entry.secret) {
      removeRaw(key);
      return null;
    }
    return {
      secret: entry.secret,
      session: typeof entry.session === 'string' && entry.session ? entry.session : null,
      expiresAt: typeof entry.expiresAt === 'string' ? entry.expiresAt : null,
    };
  };

  const writeEntry = (shareId, entry) => writeRaw(storageKeyFor(shareId), JSON.stringify(entry));

  /** A link opened again with the same secret keeps its session; a different secret starts over. */
  const rememberSecret = (shareId, secret) => {
    if (!shareId || typeof secret !== 'string' || !secret) return;
    const existing = readEntry(shareId);
    if (existing && existing.secret === secret) return;
    writeEntry(shareId, { secret, session: null, expiresAt: null });
  };

  const rememberSession = (shareId, session, expiresAt) => {
    const existing = readEntry(shareId);
    if (!existing) return;
    writeEntry(shareId, {
      secret: existing.secret,
      session: typeof session === 'string' && session ? session : null,
      expiresAt: typeof expiresAt === 'string' ? expiresAt : null,
    });
  };

  /** `{secret, session}` for the share, or null. An entry past its expiry is removed. */
  const credentialsFor = (shareId, now = Date.now()) => {
    if (!shareId) return null;
    const entry = readEntry(shareId);
    if (!entry) return null;
    const expiry = entry.expiresAt ? Date.parse(entry.expiresAt) : NaN;
    if (!Number.isNaN(expiry) && now >= expiry) {
      removeRaw(storageKeyFor(shareId));
      return null;
    }
    return { secret: entry.secret, session: entry.session };
  };

  const forget = shareId => {
    if (!shareId) return;
    removeRaw(storageKeyFor(shareId));
  };

  return { rememberSecret, rememberSession, credentialsFor, forget };
};

const browserStore = createPackageLinkStore();

export const rememberSecret = browserStore.rememberSecret;
export const rememberSession = browserStore.rememberSession;
export const credentialsFor = browserStore.credentialsFor;
export const forget = browserStore.forget;
